import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import Calendar from "react-calendar";
import { useSettings } from "../../contexts/SettingsContext";
import { db, parseSale, SaleParsed } from "../../database/db";
import { calculateBasketTotalFromSale } from "../../utils/basket_helpers";

import DiscountIcon from "@mui/icons-material/Discount";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import SummarizeIcon from "@mui/icons-material/Summarize";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import { saveAs } from "file-saver";

export interface SaleWithDetails extends SaleParsed {
  totalProducts: number;
  totalDiscounts: number;
  totalBundles: number;
  calculatedTotal: number;
}
interface Props {
  startDateToSet: Date | null;
  endDateToSet: Date | null;
}

const exportDetailedSalesReport = (sales: SaleWithDetails[]) => {
  const rows = ["Sale ID,Date,SKU,Product Name,Quantity,Price,Total,Notes"];

  sales.forEach((sale) => {
    // Add products
    sale.products.forEach((item) => {
      rows.push(
        [
          sale.id,
          sale.timestamp.toISOString(),
          item.product?.sku || "",
          `"${item.product?.name || ""}"`,
          item.quantity,
          item.priceAtTime,
          item.quantity * item.priceAtTime,
          `"${sale.notes || ""}"`,
        ].join(",")
      );
    });
  });

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  saveAs(blob, `sales_detailed_${new Date().toISOString()}.csv`);
};

const exportSalesSummary = (sales: SaleWithDetails[]) => {
  const rows = ["Sale ID,Date,Total Amount,Items,Notes"];

  sales.forEach((sale) => {
    const skus = sale.products
      .map((item) => `${item.product?.sku}(${item.quantity})`)
      .join(" ");

    rows.push(
      [
        sale.id,
        sale.timestamp.toISOString(),
        sale.salePrice,
        `"${skus}"`,
        `"${sale.notes || ""}"`,
      ].join(",")
    );
  });

  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  saveAs(blob, `sales_summary_${new Date().toISOString()}.csv`);
};
export default function AllSalesTab({ startDateToSet, endDateToSet }: Props) {
  const [startDate, setStartDate] = useState<Date | null>(startDateToSet);
  const [endDate, setEndDate] = useState<Date | null>(endDateToSet);
  const [calendarAnchor, setCalendarAnchor] = useState<{
    element: HTMLElement | null;
    type: "start" | "end";
  }>({ element: null, type: "start" });
  const [selectedSale, setSelectedSale] = useState<SaleWithDetails | null>(
    null
  );
  const [editedNotes, setEditedNotes] = useState("");
  const { settings } = useSettings();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<string> | null>(
    null
  );
  const [initialProductsLoaded, setInitialProductsLoaded] = useState(false);

  // Add this query to get all products
  const products = useLiveQuery(async () => {
    return await db.products.toArray();
  });

  const hasActiveFilters = Boolean(
    startDate ||
      endDate ||
      (products &&
        selectedProducts &&
        selectedProducts.size !== products.length)
  );

  // Initialize selected products when products are loaded
  useEffect(() => {
    if (products && !initialProductsLoaded) {
      setSelectedProducts(new Set(products.map((p) => p.sku)));
      setInitialProductsLoaded(true);
    }
  }, [products, initialProductsLoaded]);

  // Modify your existing sales query to filter by selected products
  const sales = useLiveQuery(async () => {
    let collection = db.sales.orderBy("timestamp").reverse();

    if (startDate) {
      collection = collection.filter((sale) => sale.timestamp >= startDate);
    }
    if (endDate) {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      collection = collection.filter((sale) => sale.timestamp < nextDay);
    }

    const salesArray = await collection.toArray();

    const parsedSalesArray = salesArray.map((sale) => parseSale(sale));

    // Filter sales by selected products
    console.log("selectedProducts", selectedProducts?.size);

    const filteredSales =
      selectedProducts?.size === products?.length
        ? parsedSalesArray
        : parsedSalesArray.filter((sale) => {
            console.log(
              "sale",
              sale.products.some((item) =>
                selectedProducts?.has(item.product?.sku || "")
              ),
              sale.products.map((item) => item.product?.sku),
              selectedProducts
            );
            return sale.products.some((item) =>
              selectedProducts?.has(item.product?.sku || "")
            );
          });

    const salesWithDetails = filteredSales.map((sale) => {
      const totalProducts = sale.products.reduce(
        (
          sum: number,
          { quantity, priceAtTime }: { quantity: number; priceAtTime: number }
        ) => sum + (priceAtTime > 0 ? quantity : 0),
        0
      );

      const totalDiscounts = sale.products.reduce(
        (
          sum: number,
          { quantity, priceAtTime }: { quantity: number; priceAtTime: number }
        ) => sum + (priceAtTime < 0 ? quantity : 0),
        0
      );

      const calculatedTotal = calculateBasketTotalFromSale(sale);

      return {
        ...sale,
        totalProducts,
        totalDiscounts,
        totalBundles: sale.bundles.length,
        calculatedTotal,
      } as SaleWithDetails;
    });

    return salesWithDetails;
  }, [startDate, endDate, selectedProducts]);

  const totalSales =
    sales?.reduce((sum, sale) => sum + (sale.salePrice || 0), 0) || 0;

  // Move your existing handlers here
  const handleCalendarClick = (
    event: React.MouseEvent<HTMLElement>,
    type: "start" | "end"
  ) => {
    setCalendarAnchor({ element: event.currentTarget, type });
  };

  const handleCalendarClose = () => {
    setCalendarAnchor({ element: null, type: "start" });
  };

  const handleDateChange = (date: Date) => {
    if (calendarAnchor.type === "start") {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
    handleCalendarClose();
  };

  const handleViewDetails = (sale: SaleWithDetails) => {
    setSelectedSale(sale);
    setEditedNotes(sale.notes || "");
  };

  const handleSaveNotes = async () => {
    if (selectedSale) {
      await db.sales.update(selectedSale.id, { notes: editedNotes });
      setSelectedSale(null);
    }
  };

  const handleDeleteSale = async (saleId: number) => {
    if (confirm("Are you sure you want to delete this sale?")) {
      await db.transaction("rw", [db.sales], async () => {
        await db.sales.delete(saleId);
      });
    }
  };

  const renderPrice = (actualPrice: number, calculatedPrice: number) => {
    const formattedPrice = `${settings.currencySymbol}${actualPrice.toFixed(
      settings.currencyRounding
    )}`;

    if (Math.abs(actualPrice - calculatedPrice) > 0.01) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "flex-end",
          }}>
          {formattedPrice}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}>
            (calc: {settings.currencySymbol}
            {calculatedPrice.toFixed(settings.currencyRounding)})
          </Typography>
        </Box>
      );
    }

    return formattedPrice;
  };

  const exportActions = [
    {
      icon: <ListAltIcon />,
      name: "Export Detailed",
      onClick: () => sales && exportDetailedSalesReport(sales),
    },
    {
      icon: <SummarizeIcon />,
      name: "Export Summary",
      onClick: () => sales && exportSalesSummary(sales),
    },
  ];

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    if (products) {
      setSelectedProducts(new Set(products.map((p) => p.sku)));
    }
  };

  return (
    <>
      <Accordion
        expanded={filtersExpanded}
        onChange={() => setFiltersExpanded(!filtersExpanded)}
        sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterListIcon />
            <Typography>Filters</Typography>
            {!filtersExpanded && hasActiveFilters && (
              <>
                {selectedProducts?.size !== products?.length && (
                  <Chip
                    label={`${selectedProducts?.size || 0} Products`}
                    size="small"
                    onDelete={handleClearFilters}
                  />
                )}
                {(startDate || endDate) && (
                  <Chip
                    label="Date Range"
                    size="small"
                    onDelete={handleClearFilters}
                  />
                )}
              </>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Stack direction="row" gap={2}>
              <Button
                variant="outlined"
                onClick={(e) => handleCalendarClick(e, "start")}>
                {startDate
                  ? startDate.toLocaleDateString()
                  : "Select Start Date"}
              </Button>
              <Button
                variant="outlined"
                onClick={(e) => handleCalendarClick(e, "end")}>
                {endDate ? endDate.toLocaleDateString() : "Select End Date"}
              </Button>
            </Stack>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Products
              </Typography>
              <FormGroup
                sx={{
                  maxHeight: 200,
                  overflow: "auto",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                }}>
                {products?.map((product) => (
                  <FormControlLabel
                    key={product.id}
                    control={
                      <Switch
                        checked={selectedProducts?.has(product.sku) || false}
                        onChange={(e) => {
                          const newSelected = new Set(selectedProducts || []);
                          if (e.target.checked) {
                            newSelected.add(product.sku);
                          } else {
                            newSelected.delete(product.sku);
                          }
                          setSelectedProducts(newSelected);
                        }}
                      />
                    }
                    label={
                      <Box textAlign="left">
                        <Typography
                          variant="body1"
                          color="text.primary"
                          fontWeight="bold">
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {product.sku}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>

            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<FilterListIcon />}>
              Clear All Filters
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Popover
        open={Boolean(calendarAnchor.element)}
        anchorEl={calendarAnchor.element}
        onClose={handleCalendarClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}>
        <Calendar
          onChange={(value) => handleDateChange(value as Date)}
          value={calendarAnchor.type === "start" ? startDate : endDate}
          maxDate={
            calendarAnchor.type === "start" ? endDate || undefined : undefined
          }
          minDate={
            calendarAnchor.type === "end" ? startDate || undefined : undefined
          }
        />
      </Popover>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Total Sales: {settings.currencySymbol}
        {totalSales.toFixed(settings.currencyRounding)}
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="center">Sale Amount</TableCell>
              <TableCell align="center">Products</TableCell>
              <TableCell align="center">Notes</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sales?.map((sale) => (
              <TableRow key={sale.id}>
                <TableCell>{sale.timestamp.toLocaleString()}</TableCell>
                <TableCell align="center">
                  <Typography variant="body1" fontWeight="bold">
                    {renderPrice(sale.salePrice || 0, sale.calculatedTotal)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{
                        opacity: sale.totalProducts > 0 ? 1 : 0.3,
                      }}>
                      <ShoppingBasketIcon sx={{ fontSize: "1rem" }} />
                      <Typography variant="body1">
                        {sale.totalProducts}
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{
                        opacity: sale.totalDiscounts > 0 ? 1 : 0.3,
                      }}>
                      <LocalOfferIcon sx={{ fontSize: "1.25rem" }} />
                      {sale.totalDiscounts}
                    </Stack>

                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{
                        opacity: sale.totalBundles > 0 ? 1 : 0.3,
                      }}>
                      <DiscountIcon sx={{ fontSize: "1.25rem" }} />
                      {sale.totalBundles}
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  {sale.notes || "N/A"}
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleViewDetails(sale)}>
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteSale(sale.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SpeedDial
        ariaLabel="Export Options"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
        icon={<FileDownloadIcon />}>
        {exportActions.map((action) => (
          <SpeedDialAction
            sx={{
              "& .MuiSpeedDialAction-staticTooltipLabel": {
                whiteSpace: "nowrap",
              },
            }}
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            tooltipOpen={true}
            onClick={action.onClick}
          />
        ))}
      </SpeedDial>

      <Dialog
        open={Boolean(selectedSale)}
        onClose={() => setSelectedSale(null)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>Sale Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Date: {selectedSale?.timestamp.toLocaleString()}
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSale?.products
                  .filter((item) => item.priceAtTime > 0)
                  .map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.product?.name}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {settings.currencySymbol}
                        {item.priceAtTime.toFixed(settings.currencyRounding)}
                      </TableCell>
                      <TableCell align="right">
                        {settings.currencySymbol}
                        {(item.priceAtTime * item.quantity).toFixed(
                          settings.currencyRounding
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {selectedSale?.products?.some((item) => item.priceAtTime < 0) && (
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Discount</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSale?.products
                    .filter((item) => item.priceAtTime < 0)
                    .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product?.name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {settings.currencySymbol}
                          {Math.abs(item.priceAtTime).toFixed(
                            settings.currencyRounding
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {settings.currencySymbol}
                          {Math.abs(item.priceAtTime * item.quantity).toFixed(
                            settings.currencyRounding
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {selectedSale?.bundles && selectedSale.bundles.length > 0 && (
            <TableContainer component={Paper} sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Bundle</TableCell>
                    <TableCell align="right">Discount Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSale.bundles.map((bundle, index) => (
                    <TableRow key={index}>
                      <TableCell>{bundle.name}</TableCell>
                      <TableCell align="right">
                        {settings.currencySymbol}
                        {bundle.discountAmount.toFixed(
                          settings.currencyRounding
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Stack
            direction="row"
            justifyContent="flex-end"
            gap={1}
            alignItems="baseline"
            marginY={2}>
            <Typography variant="subtitle1">
              Total: {settings.currencySymbol}
              {(selectedSale?.salePrice || 0).toFixed(
                settings.currencyRounding
              )}
            </Typography>
            {Math.abs(
              (selectedSale?.salePrice || 0) -
                (selectedSale?.calculatedTotal || 0)
            ) > 0.01 && (
              <Typography variant="body2" color="text.secondary">
                (Calculated Total: {settings.currencySymbol}
                {(selectedSale?.calculatedTotal || 0).toFixed(
                  settings.currencyRounding
                )}
                )
              </Typography>
            )}
          </Stack>

          <TextField
            label="Notes"
            multiline
            rows={4}
            value={editedNotes}
            onChange={(e) => setEditedNotes(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedSale(null)}>Cancel</Button>
          <Button onClick={handleSaveNotes} variant="contained">
            Save Notes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
